import type { SxProps, Theme } from "@mui/material";
import type { DateTime } from "luxon";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useGlobalManualLocationOrder } from "@/contexts/Staffing/GlobalManualLocationOrderContext";
import { useScheduleWeeks } from "@/hooks/useScheduleWeeks";
import { SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import type { DropdownItem } from "@/ui/Inputs/SystemDropDown/types";
import type { DepartmentLocation } from "@/utils/models";
import { sortByLocationOrder } from "@/utils/multiColumnSorting";
import { getUniqueLocationNames } from "@/utils/utilFunctions";
import { useLocationParCounts } from "@/utils/warningDialogs/useLocationParCounts";
import { buildLocationItems, type ParCountsMap, recentCount } from "./locationPickerItems";
import { NURSE_LOCATION_HISTORY_WINDOW_DAYS, useNurseLocationHistory } from "./useNurseLocationHistory";

/** Metadata about the option that was just committed, so a caller can enrich its own analytics event. */
export type LocationPickerSelectionMeta = { location: string; hasPar: boolean; belowPar: boolean; recentShifts: number; isCurrent: boolean };

/** The schedule day and shift whose staffing the par pills read. */
export type LocationPickerParDay = { date: DateTime; shiftType: string; isActual: boolean };

type LocationPickerProps = {
	locations: DepartmentLocation[];
	selectedLocation: string;
	onChange: (location: string, meta: LocationPickerSelectionMeta) => void;
	/** When set, the picker fetches this nurse's recent-shift history and shows subtitles + "works most" grouping. */
	nurseId?: number | null;
	/** Drives the "Where {name} works most" section header. */
	nurseFirstName?: string;
	/** The location the staffer is currently on — pinned first with a "Current" badge, even at 0 recent / no par. */
	currentLocation?: string;
	/** `null` where the pick isn't for one schedule day (a recurring shift): no par pills then. */
	parDay: LocationPickerParDay | null;
	disabled?: boolean;
	clearable?: boolean;
	required?: boolean;
	label?: string;
	placeholder?: string;
	formControlSx?: SxProps<Theme>;
	/** For a compact inline trigger (a list row) rather than a form field. */
	selectSx?: SxProps<Theme>;
	renderValue?: (selected: string | string[]) => ReactNode;
	menuWidth?: string | number;
	/** Fired when the picker's panel closes (pick or dismiss) — lets an inline-edit host exit edit mode. */
	onClose?: () => void;
	"data-testid"?: string;
};

/**
 * The one location picker used by every "put a shift on a location" surface. Composes the generic `SystemDropdown`
 * primitive — it does NOT fork it — and layers the scheduling domain on top: a filter box, a "N recent shifts"
 * subtitle, a "Current" badge on the staffer's present location, and a par pill (`current/target`, red when below
 * par, muted "no par" when the location has no configured target).
 *
 * Ordering: the current location is pinned first (always shown); the rest are grouped into "works most"
 * (recent shifts > 0) and "other locations", each ranked below-par first, then most-worked, then by name.
 * With no `nurseId` it degrades to a flat, unsubtitled list in the app's location order — but still
 * renders par pills when it has a `parDay`.
 */
export const LocationPicker = (props: LocationPickerProps) =>
	props.parDay ? <LocationPickerWithPar {...props} parDay={props.parDay} /> : <LocationPickerDropdown {...props} parCounts={undefined} />;

const LocationPickerWithPar = ({ parDay, ...props }: LocationPickerProps & { parDay: LocationPickerParDay }) => {
	const { date, shiftType, isActual } = parDay;
	const schedule = useScheduleWeeks(date);
	const isScheduleReady = !schedule.isLoading && !schedule.isError;
	const getParCounts = useLocationParCounts(schedule.weeks);
	// Unknown until the day's weeks load: no pills rather than every location read as 0 against par.
	const parCounts = useMemo(
		() => (isScheduleReady ? getParCounts({ date, shiftType, isActual }) : undefined),
		[isScheduleReady, getParCounts, date, shiftType, isActual],
	);
	return <LocationPickerDropdown {...props} parCounts={parCounts} />;
};

const LocationPickerDropdown = ({
	locations,
	selectedLocation,
	onChange,
	nurseId,
	nurseFirstName,
	currentLocation,
	parCounts,
	disabled,
	clearable,
	required,
	label,
	placeholder,
	formControlSx,
	selectSx,
	renderValue,
	menuWidth,
	onClose,
	"data-testid": dataTestId,
}: Omit<LocationPickerProps, "parDay"> & { parCounts: ParCountsMap | undefined }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { customStringOrderMap } = useGlobalManualLocationOrder();

	const historyQuery = useNurseLocationHistory(chosenDepartment.department_id, nurseId, NURSE_LOCATION_HISTORY_WINDOW_DAYS);
	const counts = historyQuery.data?.countsByLocation;

	// A blank-named unit is the same pick as clearing, so it isn't offered as an option of its own.
	const uniqueNames = useMemo(
		() => sortByLocationOrder(getUniqueLocationNames(locations).filter(Boolean), customStringOrderMap),
		[locations, customStringOrderMap],
	);

	const items = useMemo<DropdownItem<string>[]>(
		() => buildLocationItems(uniqueNames, nurseFirstName, { counts, parCounts, nurseId, currentLocation, t, dataTestId }),
		[uniqueNames, nurseFirstName, counts, parCounts, nurseId, currentLocation, t, dataTestId],
	);

	// A department with no locations renders nothing, matching every current caller's guard.
	if (uniqueNames.length === 0 && !currentLocation) return null;

	const metaFor = (name: string): LocationPickerSelectionMeta => {
		const par = parCounts?.[name];
		return {
			location: name,
			hasPar: !!par?.hasPar,
			belowPar: !!par?.belowPar,
			recentShifts: recentCount(name, nurseId, counts),
			isCurrent: !!currentLocation && name === currentLocation,
		};
	};

	return (
		<SystemDropdown<string>
			data-testid={dataTestId}
			label={label}
			placeholder={placeholder}
			items={items}
			// No selection (not an empty-string one) when there is no location, so the dropdown shows its placeholder.
			selectedItem={selectedLocation === "" ? undefined : selectedLocation}
			onChange={(value) => onChange(value ?? "", metaFor(value ?? ""))}
			clearable={clearable}
			required={required}
			disabled={disabled}
			loading={historyQuery.isLoading}
			fullWidth
			formControlSx={formControlSx}
			selectSx={selectSx}
			renderValue={renderValue}
			menuWidth={menuWidth}
			onClose={onClose}
			search={{ placeholder: t("locationPicker.filterPlaceholder") }}
		/>
	);
};
